import React, { useEffect, Fragment, useState, useMemo } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip, Badge } from "reactstrap";
import Swal from "sweetalert2";
import { DeleteMessage, InboxMessages } from "../../../api/messaging/messageAPI";

const Inbox = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state?.authReducer?.user);

  /* ---------------------------------
     HELPERS
  ----------------------------------*/
  const truncate = (text, length = 60) =>
    text?.length > length ? text.substring(0, length) + "..." : text;

  const isUnreadForUser = msg => {
    const userId = String(user?._id);
    return (
      msg.toUserIds?.some(u => String(u.userId) === userId && !u.isRead) ||
      msg.ccUserIds?.some(u => String(u.userId) === userId && !u.isRead)
    );
  };

  /* ---------------------------------
     FETCH INBOX
  ----------------------------------*/
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await InboxMessages();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          fromUser: item.fromUserId?.userName || "System",
          subject: item.subject || "",
          messagePreview: truncate(item.message),
          date: new Date(item.createdAt).toLocaleString(),
          isUnread: isUnreadForUser(item)
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ---------------------------------
     SEARCH
  ----------------------------------*/
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  /* ---------------------------------
     UNREAD COUNT
  ----------------------------------*/
  const unreadCount = useMemo(
    () => data.filter(d => d.isUnread).length,
    [data]
  );

  /* ---------------------------------
     ACTIONS
  ----------------------------------*/
  const handleViewClick = id => {
    navigate(`/messaging/view/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This message will be removed from your inbox",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteMessage(id);
          if (response.success) {
            showToast("success", response.message);
            setData(prev => prev.filter(item => item.id !== id));
            setFilteredData(prev => prev.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message || "Delete failed");
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /* ---------------------------------
     TABLE COLUMNS
  ----------------------------------*/
  const columns = [
    { name: "S.No", selector: row => row.index, width: "8%" },
    {
      name: "From",
      selector: row => row.fromUser,
      sortable: true,
      cell: row => (
        <span style={{ fontWeight: row.isUnread ? "bold" : "normal" }}>
          {row.fromUser}
        </span>
      ),
      width:"20%"
    },
    {
      name: "Subject",
      selector: row => row.subject,
      sortable: true,
      cell: row => (
        <span style={{ fontWeight: row.isUnread ? "bold" : "normal" }}>
          {row.subject}
        </span>
      ),
      width:"20%"
    },
    {
      name: "Message",
      selector: row => row.messagePreview,
      width:"20%"
    },
    {
      name: "Date",
      selector: row => row.date,
      sortable: true,
      width:"18%"
    },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-sm btn-primary me-2"
            onClick={() => handleViewClick(row.id)}
          >
            <i className="fa fa-eye" />
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDeleteClick(row.id)}
          >
            <i className="fa fa-trash" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      width:"15%"
    }
  ];

  /* ---------------------------------
     ROW STYLES
  ----------------------------------*/
  const conditionalRowStyles = [
    {
      when: row => row.isUnread,
      style: {
        backgroundColor: "#f1f5ff",
        fontWeight: "600"
      }
    }
  ];

  /* ---------------------------------
     CSV
  ----------------------------------*/
  const headers = [
    { label: "From", key: "fromUser" },
    { label: "Subject", key: "subject" },
    { label: "Message", key: "messagePreview" },
    { label: "Date", key: "date" }
  ];

  /* ---------------------------------
     UI
  ----------------------------------*/
  return (
    <Fragment>
      <Breadcrumb parent="Messaging" title="Inbox" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5>
              Inbox{" "}
              {unreadCount > 0 && (
                <Badge color="primary" pill>
                  {unreadCount}
                </Badge>
              )}
            </h5>

            <Link to="/messaging/send">
              <button className="btn btn-primary">Compose</button>
            </Link>
          </div>

          <div className="card-body">
            <div className="row mb-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search messages..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <CSVLink
                  data={data}
                  headers={headers}
                  filename="inbox.csv"
                  className="btn btn-primary"
                >
                  Export
                </CSVLink>
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              conditionalRowStyles={conditionalRowStyles}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Inbox;
